import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DatePicker from 'react-datepicker';
import { Switch } from '@headlessui/react';
import classNames from 'classnames';

import * as albumsService from '../../../services/api/albums';
import { useAuth } from '../../../hooks/use-auth';
import { useDebounce } from '../../../hooks/use-debounce';
import { fromJsDate, fromTimestamp } from '../../../services/formatter/date';
import { strings } from '../../../localization/strings';
import AlertPrimary from '../../../components/common/alerts/alert-primary';
import CommonAnimatedLoader from '../../../components/common/animated/loader';
import CommonButtonDefault from '../../../components/common/buttons/default';
import { Album } from '../../../entities/album';
import { Pagination } from '../../../entities/pagination';

interface ComponentProps {
  photoIds: number[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddToAlbumList({ photoIds, onClose, onSuccess }: ComponentProps) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [publish, setPublish] = useState<boolean>(true);
  const [selectedAlbumId, setSelectedAlbumId] = useState<number>();
  const [error, setError] = useState<string>();

  const onDelayedSearch = useDebounce((value: string) => setSearch(value), 500);

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    onDelayedSearch(value);
  };

  const params: Record<string, unknown> = { limit: 50, sort: '-taken_at' };

  if (search) {
    params.search = search;
  }
  if (startDate && endDate) {
    params.valid_to_start = fromJsDate(startDate);
    params.valid_to_end = fromJsDate(endDate);
  }

  const { data, isLoading, isFetching } = useQuery<{ items: Album[]; pagination: Pagination }, Error>({
    queryKey: ['albumsForPicker', search, params.valid_to_start, params.valid_to_end],
    queryFn: () => albumsService.list(token, params),
    refetchOnWindowFocus: false,
  });

  const { mutate, isLoading: isAdding } = useMutation({
    mutationFn: (albumId: number) => albumsService.addPhotos(token, albumId, photoIds, publish),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['localPhotos'] });
      await queryClient.invalidateQueries({ queryKey: ['albums'] });
      onSuccess();
      onClose();
    },
    onError: (e: unknown) => {
      if (e instanceof Error) {
        setError(e.message);
      }
    },
  });

  return (
    <div className="w-full pt-2">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600">
          {strings.album.filter.selected} {photoIds.length}
        </p>
        <div className="flex items-center space-x-3">
          <Switch
            checked={publish}
            onChange={setPublish}
            className={classNames(
              publish ? 'bg-indigo-600' : 'bg-gray-200',
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
            )}
          >
            <span className="sr-only">{strings.album.fromPhotos.publishAfterSave}</span>
            <span
              aria-hidden="true"
              className={classNames(
                publish ? 'translate-x-5' : 'translate-x-0',
                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              )}
            />
          </Switch>
          <span className="text-sm font-medium text-gray-900">{strings.album.fromPhotos.publishAfterSave}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input
          type="search"
          className="block w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-rose-500 focus:border-rose-500"
          placeholder={strings.common.searchLabel}
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <DatePicker
          monthsShown={2}
          showYearDropdown
          dateFormat="yyyy-MM-dd"
          isClearable
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
          selectsRange
          startDate={startDate}
          selected={startDate}
          endDate={endDate}
          onChange={(date) => {
            const [start, end] = date as [Date | null, Date | null];

            setStartDate(start);
            setEndDate(end);
          }}
        />
      </div>

      {!searchInput.trim() && (
        <div className="mb-4">
          <AlertPrimary size="xs" type="warning" text={strings.album.fromPhotos.latestAlbumsNotice} />
        </div>
      )}

      {error && <AlertPrimary type="danger" text={error} />}

      {isLoading || isFetching ? (
        <CommonAnimatedLoader />
      ) : (
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-md">
          {data && data.items.length > 0 ? (
            data.items.map((album) => (
              <button
                type="button"
                key={`picker-album-${album.id}`}
                aria-pressed={selectedAlbumId === album.id}
                className={classNames(
                  'w-full text-left px-4 py-3 flex items-center gap-4 disabled:opacity-50',
                  selectedAlbumId === album.id ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-500' : 'hover:bg-gray-50',
                )}
                disabled={isAdding || photoIds.length === 0}
                onClick={() => {
                  setError(undefined);
                  setSelectedAlbumId(album.id);
                }}
              >
                <span className="flex flex-1 min-w-0 flex-col">
                  <span className="font-medium text-gray-900">{album.title}</span>
                  <span className="text-xs text-gray-500">
                    {album.taken_at ? fromTimestamp(album.taken_at * 1000, true) : '-'}
                  </span>
                </span>
                <span className="flex-shrink-0 whitespace-nowrap text-xs text-gray-500">
                  {strings.album.photosCount}: {album.photos_count ?? 0}
                </span>
              </button>
            ))
          ) : (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">{strings.search.noResults}</div>
          )}
        </div>
      )}

      <div className="py-3 flex justify-end space-x-4 mt-2">
        <CommonButtonDefault type="button" secondary onClick={onClose}>
          {strings.button.cancel}
        </CommonButtonDefault>
        <CommonButtonDefault
          type="button"
          primary
          disabled={!selectedAlbumId || isAdding || photoIds.length === 0}
          isLoading={isAdding}
          onClick={() => {
            if (selectedAlbumId) {
              setError(undefined);
              mutate(selectedAlbumId);
            }
          }}
        >
          {strings.button.save}
        </CommonButtonDefault>
      </div>
    </div>
  );
}
